import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useCreateFakeThread } from '../hooks/useCreateFakeThread';
import { useInvalidateThread } from '../hooks/useInvalidateThread';
import { useRefreshThreads } from '../hooks/useRefreshThreads';
import { useRemoveThread } from '../hooks/useRemoveThread';
import { useThreadMessages } from '../hooks/useThreadMessages';
import { useThreads } from '../hooks/useThreads';
import { normalizeThreadTitle } from '../lib/threadTitle';
import type { AgentNames } from '../types';

// Types
export interface ThreadSelectOption {
  value: string;
  label: string;
  createdAt: string;
}

interface AIAssistantState {
  isExpanded: boolean;
  newThreadId: string;
  isAISidepanelOpen: boolean;
  aiSidepanelWidth: number;
  pendingInput: string | null;
  openAISidepanel: () => void;
  setPendingInput: (input: string) => void;
  setIsExpanded: (isExpanded: boolean) => void;
  setNewThreadId: (id: string) => void;
  resetNewThreadId: () => void;
  setAISidepanelWidth: (width: number) => void;
  toggleAISidepanel: () => void;
  closeAISidepanel: () => void;
  openAISidepanelWithInput: (input: string) => void;
  clearPendingInput: () => void;
}

const DEFAULT_AI_SIDEPANEL_WIDTH = 420;

// Store
const useStore = create<AIAssistantState>()(
  persist(
    (set) => ({
      isExpanded: false,
      newThreadId: crypto.randomUUID(),
      isAISidepanelOpen: false,
      aiSidepanelWidth: DEFAULT_AI_SIDEPANEL_WIDTH,
      pendingInput: null,
      setIsExpanded: (isExpanded) => set({ isExpanded }),
      setNewThreadId: (newThreadId) => set({ newThreadId }),
      resetNewThreadId: () => set({ newThreadId: crypto.randomUUID() }),
      setAISidepanelWidth: (aiSidepanelWidth) => set({ aiSidepanelWidth }),
      toggleAISidepanel: () => set((state) => ({ isAISidepanelOpen: !state.isAISidepanelOpen })),
      closeAISidepanel: () => set({ isAISidepanelOpen: false }),
      openAISidepanel: () => set({ isAISidepanelOpen: true }),
      setPendingInput: (input) => set({ pendingInput: input }),
      openAISidepanelWithInput: (input) => set({ isAISidepanelOpen: true, pendingInput: input }),
      clearPendingInput: () => set({ pendingInput: null }),
    }),
    {
      name: 'ai-assistant-sidepanel',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ aiSidepanelWidth: state.aiSidepanelWidth }),
    }
  )
);

// Constants
const AGENT_NAME = 'agentFleetManager' as AgentNames;

// Hook
export const useAIAssistant = () => {
  const {
    isExpanded,
    newThreadId,
    isAISidepanelOpen,
    aiSidepanelWidth,
    setIsExpanded,
    setNewThreadId,
    resetNewThreadId,
    setAISidepanelWidth,
    toggleAISidepanel,
    closeAISidepanel,
    pendingInput,
    openAISidepanel,
    setPendingInput,
    openAISidepanelWithInput,
    clearPendingInput,
  } = useStore();

  const { getUser, getSession } = useUser();
  const user = getUser();
  const session = getSession();
  const [threadId, setThreadId] = useQueryState('threadId', parseAsString.withDefault(''));

  const resourceId = `${user?.id}_${session?.fleet?.id}`;

  const { data: threads, isLoading: isLoadingThreads } = useThreads({
    agentName: AGENT_NAME,
    resourceId,
  });

  const {
    data: thread,
    isFetching: isFetchingThread,
    isError: isThreadError,
    refetch: refetchThread,
  } = useThreadMessages({
    agentName: AGENT_NAME,
    threadId: threadId || undefined,
  });

  useEffect(() => {
    if (isThreadError && threadId) {
      setThreadId('');
    }
  }, [isThreadError, threadId, setThreadId]);

  const { mutateAsync: invalidateThread } = useInvalidateThread();
  const { mutateAsync: refetchThreads } = useRefreshThreads();
  const { mutateAsync: createFakeThread } = useCreateFakeThread();
  const { mutateAsync: removeThread } = useRemoveThread({
    agentName: AGENT_NAME,
    resourceId,
  });

  const threadsArray = threads?.threads || [];
  const threadsOptions: ThreadSelectOption[] = threadsArray.map((t: any) => ({
    value: t.id,
    label: normalizeThreadTitle(t.title),
    createdAt: t.createdAt,
  }));

  return {
    threadId,
    threads: threadsArray,
    thread,
    threadSelected: threadsArray.find((t: any) => t.id === threadId),
    threadsOptions,
    isLoadingThreads,
    isFetchingThread,
    isExpanded,
    newThreadId,
    isAISidepanelOpen,
    aiSidepanelWidth,
    setThreadId,
    setNewThreadId,
    setIsExpanded,
    resetNewThreadId,
    setAISidepanelWidth,
    toggleAISidepanel,
    closeAISidepanel,
    pendingInput,
    openAISidepanel,
    setPendingInput,
    openAISidepanelWithInput,
    clearPendingInput,
    refetchThread,
    refetchThreads,
    removeThread,
    createFakeThread,
    invalidateThread,
  };
};
